<template>
  <fieldset class="slider-container" role="radiogroup" :aria-labelledby="showIndicator !== false ? 'role-legend' : undefined" :aria-label="showIndicator === false ? 'Select your role' : undefined">
    <legend v-if="showIndicator !== false" id="role-legend" class="slider-indicator">Sign up as...</legend>

    <!-- Visual slider track (decorative) -->
    <div class="slider-track" @click="handleSliderClick" role="presentation" aria-hidden="true">
      <div class="slider-thumb" :style="{ left: sliderThumbPosition }" aria-hidden="true"></div>
    </div>

    <!-- Accessible radio button group -->
    <div class="slider-labels" role="group" aria-label="Role selection options">
      <label class="slider-label" :class="{ active: modelValue === 'artist' }">
        <input
          type="radio"
          name="role"
          value="artist"
          :checked="modelValue === 'artist'"
          @change="updateRole('artist')"
          class="visually-hidden"
          aria-describedby="artist-description"
        />
        <span class="radio-label-text">Artist</span>
        <span id="artist-description" class="visually-hidden">Select if you are an artist looking to collaborate</span>
      </label>

      <label class="slider-label" :class="{ active: modelValue === 'both' }">
        <input
          type="radio"
          name="role"
          value="both"
          :checked="modelValue === 'both'"
          @change="updateRole('both')"
          class="visually-hidden"
          aria-describedby="both-description"
        />
        <span class="radio-label-text">Both</span>
        <span id="both-description" class="visually-hidden">Select if you are both an artist and scientist</span>
      </label>

      <label class="slider-label" :class="{ active: modelValue === 'scientist' }">
        <input
          type="radio"
          name="role"
          value="scientist"
          :checked="modelValue === 'scientist'"
          @change="updateRole('scientist')"
          class="visually-hidden"
          aria-describedby="scientist-description"
        />
        <span class="radio-label-text">Scientist</span>
        <span id="scientist-description" class="visually-hidden">Select if you are a scientist looking to collaborate</span>
      </label>
    </div>
  </fieldset>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'RoleSlider',
  props: {
    modelValue: {
      type: String,
      default: 'both',
      validator: (value) => ['artist', 'both', 'scientist'].includes(value)
    },
    showIndicator: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const sliderThumbPosition = computed(() => {
      const positions = {
        'artist': '16.67%',
        'both': '50%',
        'scientist': '83.33%'
      };
      return positions[props.modelValue] || positions.both;
    });

    const updateRole = (role) => {
      emit('update:modelValue', role);
    };

    const handleSliderClick = (e) => {
      const sliderTrack = e.currentTarget;
      const rect = sliderTrack.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = (clickX / rect.width) * 100;
      
      if (percentage < 33.33) {
        updateRole('artist');
      } else if (percentage < 66.66) {
        updateRole('both');
      } else {
        updateRole('scientist');
      }
    };

    return {
      sliderThumbPosition,
      updateRole,
      handleSliderClick
    };
  }
};
</script>

<style scoped>
/* ===== ACCESSIBILITY STYLES ===== */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

/* Enhanced focus indicators for keyboard users */
.slider-label:focus-within {
  outline: 2px solid #6566be;
  outline-offset: 2px;
  border-radius: 4px;
}

.slider-label input:focus:not(:focus-visible) {
  outline: none;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .slider-label {
    border: 2px solid currentColor;
    border-radius: 4px;
    padding: 4px;
  }

  .slider-label.active {
    background-color: currentColor;
    color: white;
  }

  .slider-track,
  .slider-thumb {
    border: 2px solid currentColor;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .slider-thumb,
  .slider-label {
    transition: none;
  }
}

/* Touch target size for mobile */
@media (max-width: 768px) {
  .slider-label {
    min-height: 44px;
    min-width: 44px;
    padding: 12px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .slider-track {
    height: 44px;
  }

  .slider-thumb {
    width: 40px;
    height: 40px;
  }
}

.slider-container {
  margin-bottom: var(--paddingStandard);
  border: none;
  padding: 0;
}

.slider-indicator {
  font-size: var(--labelText);
  font-weight: 600;
  color: var(--textColor);
  text-align: center;
  margin-bottom: var(--paddingTight);
  margin-top: 0;
  padding: 0;
  border: none;
}

.slider-track {
  position: relative;
  height: 40px;
  background: linear-gradient(to right, var(--artColor) 0%, var(--blendColor) 50%, var(--sciColor) 100%);
  border-radius: var(--stdRadius);
  margin-bottom: 12px;
  cursor: pointer;
}

.slider-thumb {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
  background: var(--bckColor);
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: left 0.3s ease;
  left: 16.67%;
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  padding: 0 8px;
}

.slider-label {
  font-size: 12px;
  color: var(--textColor);
  opacity: 0.6;
  transition: opacity 0.3s ease;
  cursor: pointer;
  text-align: center;
  flex: 1;
  position: relative;
  display: block;
}

.slider-label:hover {
  opacity: 0.8;
}

.slider-label.active {
  opacity: 1;
  font-weight: bold;
}

.radio-label-text {
  display: block;
  pointer-events: none;
}
</style>